import dayjs from "dayjs"

// randomUUID requires HTTPS; getRandomValues also works in local HTTP development.
export const createRandomId = (): string =>
  typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : Array.from(crypto.getRandomValues(new Uint8Array(16)), (value) =>
        value.toString(16).padStart(2, "0"),
      ).join("")

export function linesToList(value: string) {
  return value
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean)
}

export function roundToDecimals(value: number, decimalPlaces = 1) {
  const precision = 10 ** decimalPlaces

  return Math.round(value * precision) / precision
}

export function formatPrice(value: number, fractionDigits = 2) {
  return Number.isInteger(value) ? String(value) : value.toFixed(fractionDigits)
}

export const formatAmount = (value?: number | null): string => {
  return typeof value === "number" ? `¥${(value / 100).toFixed(2)}` : "-"
}

const formatTimestamp = (
  value: number | string | null | undefined,
  format: string,
): string => {
  if (!value) return "-"

  const date = typeof value === "number" ? dayjs.unix(value) : dayjs(value)
  return date.isValid() ? date.format(format) : "-"
}

// API 数值时间戳使用秒；字符串按原始日期格式解析。
// 保持单参数，便于直接作为 Table column 的 render 使用。
export const formatDateTime = (value?: number | string | null): string =>
  formatTimestamp(value, "YYYY-MM-DD HH:mm:ss")

export const formatDateTimeMinute = (value?: number | string | null): string =>
  formatTimestamp(value, "YYYY-MM-DD HH:mm")

export const formatValue = (value?: number | string | null) => {
  return value === undefined || value === null || value === "" ? "-" : value
}

export const getEnumLabel = <T extends number>(
  labels: Partial<Record<T, string>>,
  value?: T | null,
): string => {
  return value === undefined || value === null
    ? "-"
    : labels[value] || String(value)
}

// 表单筛选项：去掉首尾空格，空字符串视为未填写
export const optionalValue = (value?: string): string | undefined => {
  return value?.trim() || undefined
}

export function formatDurationSeconds(value: number, unit = "s") {
  return `${value.toFixed(1)}${unit}`
}

// 字节数格式化为 MB，整数不带小数
export const formatFileSize = (bytes: number): string => {
  const megabytes = bytes / 1024 / 1024
  return `${Number.isInteger(megabytes) ? megabytes : megabytes.toFixed(1)}MB`
}

export const formatSeconds = (value?: number | null): string => {
  if (typeof value !== "number" || !Number.isFinite(value)) return "-"
  return `${value.toFixed(2)} 秒`
}

export function formatTime(seconds: number, fractionDigits: 0 | 1 = 1) {
  const safeSeconds = Math.max(
    0,
    fractionDigits === 0 ? Math.floor(seconds) : roundToDecimals(seconds),
  )
  const minutes = Math.floor(safeSeconds / 60)
  const remainingSeconds = (safeSeconds - minutes * 60).toFixed(fractionDigits)

  return `${String(minutes).padStart(fractionDigits === 0 ? 1 : 2, "0")}:${remainingSeconds.padStart(fractionDigits === 0 ? 2 : 4, "0")}`
}

export function formatCreatedAtText(createdAt?: string) {
  if (!createdAt) return ""

  const date = dayjs(createdAt)

  if (!date.isValid()) return ""

  return date.format("YYYY-MM-DD HH:mm")
}

export const copyText = async (value: string): Promise<boolean> => {
  if (window.isSecureContext && navigator.clipboard) {
    try {
      await navigator.clipboard.writeText(value)
      return true
    } catch {
      // Fall back to the copy event for browsers that reject clipboard access.
    }
  }

  let copied = false
  const handleCopy = (event: ClipboardEvent) => {
    event.preventDefault()
    event.stopPropagation()

    if (!event.clipboardData) return

    event.clipboardData.clearData()
    event.clipboardData.setData("text/plain", value)
    copied = true
  }

  document.addEventListener("copy", handleCopy, true)

  try {
    document.execCommand("copy")
    return copied
  } catch {
    return false
  } finally {
    document.removeEventListener("copy", handleCopy, true)
  }
}

export function sleep(ms: number, signal?: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    const abort = () => {
      window.clearTimeout(timer)
      reject(new DOMException("请求已取消", "AbortError"))
    }
    const timer = window.setTimeout(() => {
      signal?.removeEventListener("abort", abort)
      resolve()
    }, ms)

    if (signal?.aborted) {
      abort()
      return
    }

    signal?.addEventListener("abort", abort, { once: true })
  })
}

// DOMException 在现代浏览器中继承自 Error，这里统一覆盖 fetch / ky / 媒体播放的取消错误
export const isAbortError = (error: unknown): boolean =>
  error instanceof Error && error.name === "AbortError"
